// filepath: apps/web/src/admin/tenants/DeprovisionProgress.tsx
//
// FR-TEN-6 / ISS-050: progress panel that polls the saga status and
// renders one chip per saga step. Polling stops automatically when
// the saga reaches a terminal state (TanStack Query refetchInterval
// returns false).
//
// Provides Cancel and Resume actions for the operator when the saga
// is in a cancellable / resumable state.

import React from "react";
import {
  useCancelDeprovision,
  useDeprovisionStatus,
  useResumeDeprovision,
} from "./queries";
import type { DeprovisionStatus, DeprovisionStep, SagaStatus } from "./types";

const STEP_LABELS: Record<string, string> = {
  export_data: "Export",
  delete_realm: "Realm",
  drop_schema: "Schema",
  delete_storage_prefix: "Storage",
  unregister_subdomain: "Subdomain",
};

function statusLabel(status: SagaStatus): string {
  switch (status) {
    case "running":
      return "Running";
    case "completed":
      return "Completed";
    case "compensating":
      return "Compensating";
    case "failed":
      return "Failed";
    case "cancelled":
      return "Cancelled";
    default:
      return status;
  }
}

function stepBadgeClass(step: DeprovisionStep): string {
  switch (step.status) {
    case "completed":
      return "step-chip step-chip-completed";
    case "failed":
      return "step-chip step-chip-failed";
    case "compensated":
      return "step-chip step-chip-compensated";
    case "pending":
    default:
      return "step-chip step-chip-pending";
  }
}

export interface DeprovisionProgressProps {
  tenantId: string;
}

export function DeprovisionProgress({
  tenantId,
}: DeprovisionProgressProps): React.ReactElement {
  const statusQuery = useDeprovisionStatus(tenantId);
  const cancelMut = useCancelDeprovision(tenantId);
  const resumeMut = useResumeDeprovision(tenantId);

  if (statusQuery.isLoading) {
    return (
      <div data-testid="deprovision-progress-loading">Loading saga status…</div>
    );
  }
  if (statusQuery.isError) {
    return (
      <div
        data-testid="deprovision-progress-error"
        role="alert"
        aria-live="assertive"
      >
        Failed to load saga status: {String(statusQuery.error)}
      </div>
    );
  }
  const data = statusQuery.data as DeprovisionStatus;
  return (
    <section
      data-testid="deprovision-progress"
      className="deprovision-progress"
    >
      <header>
        <h2>De-provisioning saga</h2>
        <p aria-live="polite" data-testid="deprovision-progress-status">
          <strong>Status:</strong> {statusLabel(data.status)}
          {data.error_msg ? (
            <span className="deprovision-error"> — {data.error_msg}</span>
          ) : null}
        </p>
      </header>

      <ol className="step-chip-row" aria-label="Saga steps">
        {(data.steps || []).map((s) => (
          <li
            key={s.step_index}
            className={stepBadgeClass(s)}
            data-testid={`step-chip-${s.step_index}`}
          >
            <span className="step-chip-label">
              {STEP_LABELS[s.step_name] || s.step_name}
            </span>
            <span className="step-chip-status">{s.status}</span>
            {s.error_msg ? (
              <span className="step-chip-error">{s.error_msg}</span>
            ) : null}
          </li>
        ))}
      </ol>

      <footer>
        {data.status === "running" ? (
          <button
            type="button"
            onClick={() => cancelMut.mutate()}
            disabled={cancelMut.isPending}
            data-testid="deprovision-cancel"
          >
            {cancelMut.isPending ? "Cancelling…" : "Cancel saga"}
          </button>
        ) : null}
        {data.status === "failed" ? (
          <button
            type="button"
            onClick={() => resumeMut.mutate()}
            disabled={resumeMut.isPending}
            data-testid="deprovision-resume"
          >
            {resumeMut.isPending ? "Resuming…" : "Resume saga"}
          </button>
        ) : null}
        {data.status === "completed" || data.status === "cancelled" ? (
          <p data-testid="deprovision-progress-done">
            Saga is in terminal state: {data.status}.
          </p>
        ) : null}
      </footer>
    </section>
  );
}
